import { ApiError, RequestError } from '@app/packages/api/errors'
import { ApiError as InternalApiError, RequestError as InternalRequestError } from '@app/packages/internalApi/errors'

export class SerializableError extends Error {
  static readonly key = 'error:52CFB8B5-151A-40DB-BFB0-958D2CF83862'

  static create(err: Error) {
    const error = new SerializableError(err.message)
    error.name = err.name
    error.stack = err.stack
    error.cause = err
    return error
  }

  toJSON() {
    const originalError = this.cause as Error
    const kind = KINDS.find(k => k.matches(originalError))

    return {
      key: SerializableError.key,
      message: originalError.message,
      name: originalError.name,
      ...(kind ? { type: kind.type, ...kind.toData(originalError as any) } : {}),
    }
  }

  /** Rebuilds the error class the server sent, so the client can narrow on it the same way. */
  static fromData(data: any) {
    try {
      if (data.key !== SerializableError.key) return undefined
      const kind = KINDS.find(k => k.type === data.type)
      const err = kind ? kind.fromData(data) : new Error(data.message)
      err.stack = ''

      return err
    } catch {
      return undefined
    }
  }
}

type ErrorKind<E extends Error> = {
  type: string
  matches: (e: Error) => e is E
  toData: (e: E) => Record<string, unknown>
  fromData: (data: any) => Error
}

const KINDS: ErrorKind<any>[] = [
  {
    type: 'apierror:164AEAEE-3BFB-459D-BD7D-6D44828D8EAE',
    matches: (e): e is ApiError => e instanceof ApiError,
    toData: (e: ApiError) => ({ description: e.description, status: e.status, response: e.response }),
    fromData: data => ApiError.create(data.status, data.description, data.response),
  },
  {
    type: 'requesterror:CD884ED4-CBB6-4C0A-9E88-6CF6DA56912E',
    matches: (e): e is RequestError => e instanceof RequestError,
    toData: (e: RequestError) => ({ description: e.description, status: e.status, body: e.body }),
    fromData: data => RequestError.create(data.status, data.description, data.body),
  },
  {
    type: 'internalapierror:FDB1A07F-BF0B-4474-A1CB-70136DE044E9',
    matches: (e): e is InternalApiError => e instanceof InternalApiError,
    toData: (e: InternalApiError) => ({ description: e.description, status: e.status, response: e.response }),
    fromData: data => InternalApiError.create(data.status, data.description, data.response),
  },
  {
    type: 'internalrequesterror:980FF3E1-E197-4567-BEF1-BE076C7F4473',
    matches: (e): e is InternalRequestError => e instanceof InternalRequestError,
    toData: (e: InternalRequestError) => ({ description: e.description, status: e.status, body: e.body }),
    fromData: data => InternalRequestError.create(data.status, data.description, data.body),
  },
]
